import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineCalendarDays,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineEye,
  HiOutlineCog6Tooth,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineShieldCheck,
} from "react-icons/hi2";
import "./Schedule.css";
import SharedModal from "../../../components/common/SharedModal";
import Submitsbo from "./Submitsbo";
import CreateSchedule from "./CreateSchedule";
import ObservationPreview from "./ObservationPreview";
import safetyObservationService from "../../../services/safetyObservationService";
import userService from "../../../services/userService";
import plantService from "../../../services/plantService";
import PageLoading from "../../../components/common/PageLoading";

const Schedule = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoundId, setSelectedRoundId] = useState(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [isCreateScheduleOpen, setIsCreateScheduleOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewRoundId, setPreviewRoundId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  // ... rest of state stays same

  // Data
  const [rounds, setRounds] = useState([]);
  const [users, setUsers] = useState([]);
  const [plants, setPlants] = useState([]);

  // Filters
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [filters, setFilters] = useState({
    year: String(currentYear),
    month: String(currentMonth),
    employeeId: "",
    plantId: "",
  });

  // Fetch rounds when filters change
  const fetchRounds = useCallback(async () => {
    if (!filters.year || !filters.month) {
      setRounds([]);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const year = parseInt(filters.year, 10);
      const month = parseInt(filters.month, 10);
      const employeeId = filters.employeeId
        ? parseInt(filters.employeeId, 10)
        : null;
      const plantId = filters.plantId ? parseInt(filters.plantId, 10) : null;

      const result = await safetyObservationService.getSchedules(
        year,
        month,
        employeeId,
        plantId,
      );

      setRounds(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error("Error fetching schedules:", err);
      setError("Failed to load schedules. Please try again.");
      setRounds([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchUsers();
    fetchPlants();
  }, []);

  useEffect(() => {
    fetchRounds();
  }, [fetchRounds]); // Depend on fetchRounds which depends on filters

  const fetchUsers = async () => {
    try {
      const data = await userService.getAllUsers();
      setUsers(data || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchPlants = async () => {
    try {
      const data = await plantService.getAllPlants();
      setPlants(data || []);
      
      // If we don't have a plant selected but there's only one, auto-select it
      if (!filters.plantId && data?.length === 1) {
        setFilters(prev => ({ ...prev, plantId: data[0].plantId.toString() }));
      }
    } catch (error) {
      console.error("Failed to fetch plants:", error);
    }
  };

  // Search / filter (client side search among fetched schedules)
  const filteredRounds = rounds.filter((round) => {
    const employeeName = round.employeeName || "";
    const description = round.description || "";
    const obsNumber = round.observationNumber || "";
    const term = searchTerm.toLowerCase();

    return (
      employeeName.toLowerCase().includes(term) ||
      description.toLowerCase().includes(term) ||
      obsNumber.toLowerCase().includes(term)
    );
  });

  const getStatusClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "active" || s === "open" || s === "completed") return s;
    return "active";
  };

  const handleAddClick = (roundId, scheduleId) => {
    setSelectedRoundId(roundId || null);
    setSelectedScheduleId(scheduleId || null);
    setIsModalOpen(true);
  };

  const handlePreviewClick = (roundId) => {
    setPreviewRoundId(roundId);
    setIsPreviewOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedRoundId(null);
    setSelectedScheduleId(null);
    fetchRounds();
  };

  const handleDelete = async (scheduleId, roundName) => {
    setDeleteConfirm({ id: scheduleId, name: roundName || "this schedule" });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await safetyObservationService.deleteSchedule(deleteConfirm.id);
      setDeleteConfirm(null);
      toast.success("Schedule deleted successfully!");
      fetchRounds(); // Refresh list
    } catch (err) {
      console.error("Error deleting schedule:", err);
      toast.error("Failed to delete schedule.");
      setDeleteConfirm(null);
    }
  };

  const MONTHS = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const yearOptions = [];
  for (let y = currentYear + 1; y >= currentYear - 4; y--) {
    yearOptions.push(y);
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const selectedMonthLabel =
    MONTHS.find((m) => m.value === parseInt(filters.month))?.label || "";

  const getGroupedRounds = () => {
    const groups = {};
    filteredRounds.forEach((round) => {
      const date = new Date(round.scheduledDate);
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      const weekNumber = Math.ceil((date.getDate() + firstDay.getDay()) / 7);
      const monthLabel = date.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
      const weekKey = `Week-${weekNumber} ${monthLabel}`;

      if (!groups[weekKey]) groups[weekKey] = {};

      const plantName = round.plantName || "Unknown Plant";
      const deptName = round.departmentName || "General Dept";
      const areaName = round.area || "General Area";
      const locationKey = `${plantName} - ${deptName} - ${areaName}`;

      if (!groups[weekKey][locationKey]) {
        groups[weekKey][locationKey] = {
          plant: plantName,
          dept: deptName,
          area: areaName,
          status: round.status,
          rounds: [],
          totalPeople: 0,
          totalSafe: 0,
          totalUnsafe: 0,
        };
      }

      groups[weekKey][locationKey].rounds.push(round);
      groups[weekKey][locationKey].totalPeople +=
        round.peopleContactedCount || 0;
      groups[weekKey][locationKey].totalSafe += round.safeCount || 0;
      groups[weekKey][locationKey].totalUnsafe += round.unsafeCount || 0;
    });
    return groups;
  };

  return (
    <div className="pagecontainer">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Scheduled SO Rounds</h1>
          <p>Manage and track safety behavior observation rounds</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={() => handleAddClick(null, null)}
          >
            <HiOutlinePlus size={20} />
            Add Observation Round
          </button>
          <button
            className="btn btn-primary accent"
            onClick={() => setIsCreateScheduleOpen(true)}
          >
            <HiOutlineCalendarDays size={20} />
            Create Schedule
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Filters & Actions Bar (Setu Style) */}
      <div className="filter-actions-bar-setu">
        <div className="filter-controls-setu">
          <select
            name="plantId"
            value={filters.plantId}
            onChange={handleFilterChange}
            className="filter-select-setu"
          >
            <option value="">Select Plant</option>
            {plants.map((p) => (
              <option key={p.plantId || p.id} value={p.plantId || p.id}>
                {p.plantName}
              </option>
            ))}
          </select>

          <select
            name="year"
            value={filters.year}
            onChange={handleFilterChange}
            className="filter-select-setu"
          >
            <option value="">Select Year</option>
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          <select
            name="month"
            value={filters.month}
            onChange={handleFilterChange}
            className="filter-select-setu"
          >
            <option value="">Select Month</option>
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        <div className="search-actions-setu">
          <div className="search-wrapper-setu">
            <span className="search-icon-setu">
              <HiOutlineMagnifyingGlass />
            </span>
            <input
              type="text"
              placeholder="Search Here..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-setu"
            />
          </div>
          <button
            className="settings-btn-setu"
            onClick={() => navigate("/home/safety-observation/settings")}
            title="Settings"
          >
            <HiOutlineCog6Tooth />
          </button>
        </div>
      </div>

      <div className="count-badge">
        {loading && rounds.length === 0 ? (
          <PageLoading variant="muted" message="Loading…" />
        ) : (
          <span>
            {filteredRounds.length}{" "}
            {filteredRounds.length === 1 ? "round" : "rounds"}
          </span>
        )}
      </div>

      <div className="report-groups">
        {loading && rounds.length === 0 ? (
          <PageLoading variant="compact" message="Loading observation rounds…" />
        ) : filteredRounds.length === 0 ? (
          <div className="empty-state">
            <HiOutlineCalendarDays className="empty-icon" />
            <h3>No observation rounds found</h3>
            <p>
              {searchTerm
                ? "Try adjusting your search terms"
                : `No rounds for ${selectedMonthLabel} ${filters.year}. Create a new round to get started.`}
            </p>
          </div>
        ) : (
          Object.entries(getGroupedRounds()).map(([weekKey, locations]) => (
            <div key={weekKey} className="week-group">
              <div className="week-header-simple">{weekKey}</div>

              {Object.entries(locations).map(([locationKey, data]) => {
                const isExpanded = expandedGroups[locationKey] === true; // Default false
                const toggleExpand = () =>
                  setExpandedGroups((prev) => ({
                    ...prev,
                    [locationKey]: !isExpanded,
                  }));

                return (
                  <div key={locationKey} className="location-card-container">
                    <div
                      className="location-card-header-v2 cursor-pointer"
                      onClick={toggleExpand}
                    >
                      <div className="header-left-v2">
                        <div className="icon-box-v2 chevron">
                          <HiOutlineChevronDown
                            className={isExpanded ? "" : "collapsed-icon"}
                          />
                        </div>
                        <div className="icon-box-v2 calendar">
                          <HiOutlineCalendarDays />
                        </div>
                        <div className="header-titles-v2">
                          <div className="location-title-v2">{locationKey}</div>
                          <div className="status-v2">
                            Status:{" "}
                            <span className={getStatusClass(data.status)}>
                              {data.status || "Scheduled"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="header-right-v2">
                        <div className="summary-stats-v2">
                          <div className="stat-block-v2 people">
                            <span className="stat-label-v2">
                              People Contacted
                            </span>
                            <span className="stat-value-v2">
                              {data.totalPeople}
                            </span>
                          </div>
                          <div className="stat-block-v2 safebox">
                            <span className="stat-label-v2">
                              Safe Observation
                            </span>
                            <span className="stat-value-v2">
                              {data.totalSafe}
                            </span>
                          </div>
                          <div className="stat-block-v2 unsafebox">
                            <span className="stat-label-v2">
                              Unsafe Observation
                            </span>
                            <span className="stat-value-v2">
                              {data.totalUnsafe}
                            </span>
                          </div>
                        </div>
                        <div
                          className="btn btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddClick(null, null);
                          }}
                        >
                          <HiOutlinePlus /> Add
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="location-card-body-v2">
                        <table className="exact-pro-table">
                          <thead>
                            <tr>
                              <th>Observation Number</th>
                              <th>Date</th>
                              <th>Time</th>
                              <th className="text-center">People Contacted</th>
                              <th className="text-center">Safe Observations</th>
                              <th className="text-center">
                                Unsafe Observations
                              </th>
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.rounds.map((round) => (
                              <tr key={round.scheduleId}>
                                <td>
                                  <span
                                    className="obs-number-link"
                                    onClick={() => {
                                      if (round.observationNumber) {
                                        handlePreviewClick(round.roundId);
                                      } else {
                                        handleAddClick(
                                          round.roundId,
                                          round.scheduleId,
                                        );
                                      }
                                    }}
                                  >
                                    {round.observationNumber && (
                                      <HiOutlineShieldCheck className="obs-icon" />
                                    )}
                                    {round.observationNumber || "Pending"}
                                  </span>
                                </td>
                                <td>
                                  {round.scheduledDate
                                    ? new Date(
                                      round.scheduledDate,
                                    ).toLocaleDateString("en-GB")
                                    : "-"}
                                </td>
                                <td>{round.time || "No Time"}</td>
                                <td className="text-center">
                                  {round.peopleContactedCount}
                                </td>
                                <td className="text-center">
                                  {round.safeCount}
                                </td>
                                <td className="text-center">
                                  {round.unsafeCount}
                                </td>
                                <td className="text-center">
                                  <div className="action-buttons">
                                    <div
                                      className="btn-icon-edit cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddClick(
                                          round.roundId,
                                          round.scheduleId,
                                        );
                                      }}
                                      title="Edit Observation"
                                    >
                                      <HiOutlinePencil size={18} />
                                    </div>
                                    <div
                                      className={`btn-icon-view cursor-pointer ${!round.observationNumber ? "opacity-50 cursor-not-allowed" : ""}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (round.observationNumber) {
                                          handlePreviewClick(round.roundId);
                                        }
                                      }}
                                      title="Preview Observation"
                                    >
                                      <HiOutlineEye size={18} />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button className="btn-danger" onClick={confirmDelete}>
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.name}</strong>?
          </p>
          <p className="warning-text">This action cannot be undone.</p>
        </div>
      </SharedModal>

      {/* Submitsbo Side Drawer */}
      <Submitsbo
        isOpen={isModalOpen}
        onClose={handleModalClose}
        roundId={selectedRoundId}
        scheduleId={selectedScheduleId}
      />

      {/* Create Schedule Modal */}
      <CreateSchedule
        isOpen={isCreateScheduleOpen}
        onClose={() => {
          setIsCreateScheduleOpen(false);
          fetchRounds();
        }}
      />

      {/* Observation Preview Modal */}
      {isPreviewOpen && (
        <ObservationPreview
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          roundId={previewRoundId}
        />
      )}
    </div>
  );
};

export default Schedule;
